import Link from "next/link";
import { useParams, usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";

import Fuse from "fuse.js";
import { RiHome3Line } from "react-icons/ri";

import { useLeftSideBarStore } from "~/state/store";
import { cn } from "~/utils/fontsConfig";

import { api } from "~/utils/api";
import ProposeEventModal from "../../modals/ProposeEventModal";
import SubNavigationGroup from "../../navigation/SubNavigationGroup";
import { Card } from "../../shadcn/Card";
import { getSideBarItemsForPreapprovedHost } from "./SidebarItems";
import { ModalType, SidebarItems } from "./SidebarTypes";
import { UserPermissions } from "~/server/api/helpers/permissionHelpers";
import { If } from "../../Conditional";

const CommunityPreapprovedHostManagementBar = ({
    permissions,
}: {
    permissions: UserPermissions;
}) => {
    const params = useParams<{ communitySlug: string; slug: string }>();
    const pathname = usePathname() ?? "";

    const { setSideBarState, sideBarState } = useLeftSideBarStore();

    const { communitySlug, slug } = params || {
        communitySlug: undefined,
        slug: undefined,
    };
    const { data: community } = api.community.getSimpleCommunityBySlug.useQuery(
        {
            communtiyslug: communitySlug as string,
        },
        {
            enabled: !!communitySlug,
        },
    );
    const [actualSlug, setActualSlug] = useState<string | undefined>();

    useEffect(() => {
        if (slug) {
            setActualSlug(slug as string);
        }

        if (communitySlug) {
            setActualSlug(communitySlug as string);
        }
    }, [communitySlug, slug]);

    const [openModalType, setOpenModalType] = useState<ModalType>();
    const updateModalType = useCallback((type: ModalType) => {
        setOpenModalType(type);
    }, []);

    const SIDEBAR_ITEMS = useMemo(
        () =>
            getSideBarItemsForPreapprovedHost(
                actualSlug as string,
                updateModalType,
            ),
        [actualSlug],
    );

    const [searchTerm, setSearchTerm] = useState("");

    const flattenedItems = useMemo(() => {
        return Object.values(SIDEBAR_ITEMS).flatMap((section) =>
            section.items.map((item) => ({ ...item, section })),
        );
    }, [SIDEBAR_ITEMS]);

    const fuse = useMemo(
        () =>
            new Fuse(flattenedItems, {
                keys: ["name", "section"],
                threshold: 0.4,
            }),
        [flattenedItems],
    );

    const filteredItems = useMemo(() => {
        if (!searchTerm) return SIDEBAR_ITEMS;

        const searchResults = fuse.search(searchTerm);
        const groupedResults = searchResults.reduce(
            (acc: SidebarItems, { item }) => {
                if (!acc[item.section.title]) {
                    acc[item.section.title] = {
                        title: item.section.title,
                        Icon: item.section.Icon,
                        items: [],
                    };
                }

                acc[item.section.title]?.items.push(item);

                return acc;
            },
            {},
        );

        return groupedResults;
    }, [searchTerm, SIDEBAR_ITEMS, fuse]);

    const isHome = pathname === `/${actualSlug}`;

    return (
        <>
            <>
                <span className="md:overflow-none md:dunset block h-screen overflow-y-auto md:h-auto">
                    <div className="flex w-full max-w-[300px] flex-col gap-4 rounded-[10px] bg-v3-dark-gray p-3 md:my-4 md:ml-4">
                        {/* Home Card */}
                        <Card className="gap-1 rounded-lg bg-v3-card-lighter-gray p-4 px-2 py-2">
                            <div
                                className={cn(
                                    "grid grid-cols-[10%_90%] items-center justify-center rounded-lg px-4 py-2 hover:bg-v3-N-400",
                                    {
                                        "bg-v3-N-400 text-white": isHome,
                                    },
                                )}
                            >
                                <RiHome3Line className="text-lg" />
                                <Link href={`/${actualSlug}`}>Home</Link>
                            </div>
                        </Card>

                        {Object.entries(filteredItems).map(([key, section]) => (
                            <SubNavigationGroup
                                key={key}
                                section={section}
                            ></SubNavigationGroup>
                        ))}
                        <div className="block h-24 md:hidden" />
                    </div>
                </span>

                {community && (
                    <ProposeEventModal
                        community={community}
                        setOpen={(open: boolean) =>
                            setOpenModalType(open ? "new-event" : undefined)
                        }
                        open={openModalType === "new-event"}
                        hasManagingPermissions={true}
                    />
                )}
            </>
            <If condition={sideBarState !== "closed"}>
                <div
                    className="absolute left-[300px] top-0 z-10 h-full w-full"
                    onClick={() => setSideBarState("closed")}
                />
            </If>
        </>
    );
};

export default CommunityPreapprovedHostManagementBar;
